import React from "react";
import { Loader2 } from "lucide-react";

interface AllotableResourcePermission {
  resourceId: number;
  resourceName: string;
  actionName: string;
  permissionIds: number[];
}

interface Role {
  roleResourceId: number;
  role: string;
}

interface PermissionMap {
  [resourceId: string]: { read: boolean; write: boolean };
}

interface RolesTabProps {
  roles: Role[];
  selectedRoleId: number | null;
  uniqueResources: AllotableResourcePermission[];
  permissions: PermissionMap | undefined;
  loading: boolean;
  onRoleChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onPermissionToggle: (permissionType: "read" | "write", resourceId: number) => void;
  onAddRole: () => void;
}

export const RolesTab: React.FC<RolesTabProps> = ({
  roles,
  selectedRoleId,
  uniqueResources,
  permissions,
  loading,
  onRoleChange,
  onPermissionToggle,
  onAddRole,
}) => {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-gray-900">Roles & Permissions</h3>
        </div>
        <button
          onClick={onAddRole}
          className="inline-flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-md hover:bg-brand/80 transition-colors"
        >
          Add Role
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="flex items-center gap-2">
            <Loader2 className="w-8 h-8 animate-spin text-brand" />
            <span className="text-gray-500">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-cardborder">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <div className="w-64">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Role</label>
                <select
                  value={selectedRoleId ?? ""}
                  onChange={onRoleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                >
                  {roles.map((role) => (
                    <option key={role.roleResourceId} value={role.roleResourceId}>
                      {role.role}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 border-b border-gray-200">
                      Resource
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 border-b border-gray-200">
                      Create/Update/Delete
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 border-b border-gray-200">
                      Read
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {uniqueResources.length > 0 ? (
                    uniqueResources.map((resource) => {
                      const rolePermission = permissions
                        ? permissions[resource.resourceId.toString()]
                        : undefined;
                      return (
                        <tr
                          key={resource.resourceId}
                          className="hover:bg-gray-50 border-b border-gray-200 last:border-b-0"
                        >
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {resource.resourceName}
                          </td>
                          <td className="px-6 py-4">
                            <input
                              type="checkbox"
                              checked={rolePermission ? rolePermission.write : false}
                              onChange={() => onPermissionToggle("write", resource.resourceId)}
                              className="w-4 h-4 text-brand border-gray-300 rounded focus:ring-brand"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <input
                              type="checkbox"
                              checked={rolePermission ? rolePermission.read : false}
                              onChange={() => onPermissionToggle("read", resource.resourceId)}
                              className="w-4 h-4 text-brand border-gray-300 rounded focus:ring-brand"
                            />
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={3}
                        className="px-6 py-4 text-sm text-gray-500 text-center"
                      >
                        No resources found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
